import { useNavigate } from 'react-router';
import type { Stage } from '@/domain';
import { useBoard } from '@/application/usecases/useBoard';
import { useLineLayout } from '@/application/usecases/useLineLayout';
import { strings } from '@/shared/strings';
import { ProductionLine } from '@/ui/data/ProductionLine';
import { ReworkIcon } from '@/ui/icons';
import { SkeletonCards } from '@/ui/feedback/Loading';
import { AdminFigures } from '../orders/AdminFigures';
import styles from './LineScreen.module.css';

/**
 * Production line. Section 5.2.
 *
 * The whole screen is the line plus the caption the client asked for, because
 * the line is the screen. Detail lives one tap away in stage detail.
 *
 * The line is drawn left to right, or top to bottom for somebody who pressed
 * "Vertical view" on this device. The request of 5 October 2026 (third),
 * section 9.
 */
export function LineScreen() {
  const navigate = useNavigate();
  const { data, isPending, isError } = useBoard();
  // Left to right or top to bottom: one choice, shared with the Overview.
  const { vertical, setVertical } = useLineLayout();

  function openStage(stage: Stage) {
    void navigate(`/line/${stage}`);
  }

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.line.label}</span>
        <h2>{strings.line.title}</h2>
        <span className={styles.hint}>{strings.line.hint}</span>
      </div>

      {/* Active, urgent, overdue and on hold, for the admin. The lab's request of 17 October 2026, item 5. */}
      <AdminFigures screen="line" />

      {isError && <p className={styles.hint}>{strings.errors.server}</p>}
      {isPending && <SkeletonCards cards={6} label={strings.common.loadingBoard} />}

      {data !== undefined && (
        <>
          <ProductionLine
            stages={data.stages}
            activeKits={data.activeKits}
            onOpenStage={openStage}
            logistics={data.logistics}
            onOpenLogistics={() => void navigate('/line/logistics')}
            vertical={vertical}
            onVerticalChange={setVertical}
          />

          {/*
            Section 5.2 asks for this caption by name. It is the one place the
            system explains that the line is not a fixed route, which is what
            stops somebody reading it as a workflow diagram.
          */}
          <p className={styles.note}>
            <ReworkIcon />
            <span>{strings.line.note}</span>
          </p>
        </>
      )}
    </div>
  );
}
